/**
 * Convert an open enquiry into a lead. A lead needs a phone number, so the
 * phone is required here even though the enquiry could be logged without
 * one. On success the realtor lands on the new lead.
 */

import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useConvertEnquiry } from "@/features/realtor/hooks/use-realtor-crm";
import {
	type LeadType,
	PIPELINE_STAGES,
	type PipelineStage,
} from "@/features/realtor/services/leads-api.service";
import type { Enquiry } from "@/features/realtor/services/realtor-crm-api.service";
import { formatPaiseAsRupeeInput } from "@/features/realtor/utils/listings-format";
import { StudioButton } from "@/features/studio/theme";
import {
	ChoiceChips,
	FormField,
	isAcceptablePhone,
	parseBudgetRange,
	SELECT_CLASS,
} from "./enquiry-shared";

const LEAD_TYPES: readonly LeadType[] = ["buyer", "seller"];

// A lead converted from an enquiry is still in play, so the closed stages
// are not offered as a starting point.
const STARTING_STAGES: readonly PipelineStage[] = PIPELINE_STAGES.map(
	(stage) => stage.id,
).filter((id) => id !== "closed_won" && id !== "closed_lost");

interface ConvertEnquiryDialogProps {
	/** The enquiry to convert; the dialog is closed while this is null. */
	enquiry: Enquiry | null;
	onOpenChange: (open: boolean) => void;
}

export function ConvertEnquiryDialog({
	enquiry,
	onOpenChange,
}: ConvertEnquiryDialogProps): ReactElement {
	const { t } = useTranslation();
	return (
		<Dialog open={enquiry != null} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-xl border-dashboard-border bg-dashboard-bg sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="font-display text-2xl font-medium italic text-dashboard-text">
						{t("realtor.enquiries.convert.title")}
					</DialogTitle>
					<DialogDescription className="text-dashboard-muted">
						{t("realtor.enquiries.convert.description")}
					</DialogDescription>
				</DialogHeader>
				{enquiry && (
					<ConvertEnquiryForm
						key={enquiry.id}
						enquiry={enquiry}
						onCancel={() => onOpenChange(false)}
						onConverted={() => onOpenChange(false)}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}

interface FormState {
	name: string;
	phone: string;
	type: LeadType;
	pipelineStage: PipelineStage;
	budgetMinInput: string;
	budgetMaxInput: string;
	notes: string;
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

function initialState(enquiry: Enquiry): FormState {
	return {
		name: enquiry.name,
		phone: enquiry.phone ?? "",
		type: "buyer",
		pipelineStage: "new",
		budgetMinInput:
			enquiry.budgetMin != null
				? formatPaiseAsRupeeInput(enquiry.budgetMin)
				: "",
		budgetMaxInput:
			enquiry.budgetMax != null
				? formatPaiseAsRupeeInput(enquiry.budgetMax)
				: "",
		notes: enquiry.intent ?? "",
	};
}

function ConvertEnquiryForm({
	enquiry,
	onCancel,
	onConverted,
}: {
	enquiry: Enquiry;
	onCancel: () => void;
	onConverted: () => void;
}): ReactElement {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [form, setForm] = useState<FormState>(() => initialState(enquiry));
	const [errors, setErrors] = useState<FieldErrors>({});
	const convert = useConvertEnquiry();

	const setField = <K extends keyof FormState>(
		key: K,
		value: FormState[K],
	): void => {
		setForm((prev) => ({ ...prev, [key]: value }));
		setErrors((prev) => {
			if (!prev[key]) return prev;
			const next = { ...prev };
			delete next[key];
			return next;
		});
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		const nextErrors: FieldErrors = {};
		const name = form.name.trim();
		const phone = form.phone.trim();
		if (!name) nextErrors.name = t("realtor.enquiries.convert.errors.name");
		if (!phone) {
			nextErrors.phone = t("realtor.enquiries.convert.errors.phoneRequired");
		} else if (!isAcceptablePhone(phone)) {
			nextErrors.phone = t("realtor.enquiries.errors.phone");
		}
		const budget = parseBudgetRange(form.budgetMinInput, form.budgetMaxInput);
		if (budget.minError) {
			nextErrors.budgetMinInput = t(
				`realtor.enquiries.errors.${budget.minError}`,
			);
		}
		if (budget.maxError) {
			nextErrors.budgetMaxInput = t(
				`realtor.enquiries.errors.${budget.maxError}`,
			);
		}
		setErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) return;

		convert.mutate(
			{
				id: enquiry.id,
				input: {
					name,
					phone,
					phoneCountryCode: enquiry.phoneCountryCode,
					type: form.type,
					pipelineStage: form.pipelineStage,
					budgetMin: budget.budgetMin,
					budgetMax: budget.budgetMax,
					notes: form.notes.trim() || null,
				},
			},
			{
				onSuccess: ({ lead }) => {
					toast.success(t("realtor.enquiries.convert.successToast", { name }));
					onConverted();
					navigate(`/realtor/leads/${lead.id}`);
				},
			},
		);
	};

	return (
		<form onSubmit={handleSubmit} noValidate className="space-y-4">
			<FormField
				id="convert-name"
				label={t("realtor.enquiries.convert.nameLabel")}
				error={errors.name}
			>
				<Input
					id="convert-name"
					value={form.name}
					onChange={(e) => setField("name", e.target.value)}
					maxLength={200}
					aria-invalid={errors.name ? true : undefined}
				/>
			</FormField>

			<FormField
				id="convert-phone"
				label={t("realtor.enquiries.convert.phoneLabel")}
				error={errors.phone}
				hint={t("realtor.enquiries.convert.phoneHint")}
			>
				<Input
					id="convert-phone"
					type="tel"
					inputMode="tel"
					value={form.phone}
					onChange={(e) => setField("phone", e.target.value)}
					placeholder="98765 43210"
					maxLength={20}
					aria-invalid={errors.phone ? true : undefined}
				/>
			</FormField>

			<ChoiceChips
				name="convert-type"
				legend={t("realtor.enquiries.convert.typeLabel")}
				value={form.type}
				onChange={(type) => setField("type", type)}
				options={LEAD_TYPES.map((type) => ({
					value: type,
					label: t(`realtor.leads.types.${type}`),
				}))}
			/>

			<FormField
				id="convert-stage"
				label={t("realtor.enquiries.convert.stageLabel")}
			>
				<select
					id="convert-stage"
					className={SELECT_CLASS}
					value={form.pipelineStage}
					onChange={(e) =>
						setField("pipelineStage", e.target.value as PipelineStage)
					}
				>
					{STARTING_STAGES.map((stage) => (
						<option key={stage} value={stage}>
							{t(`realtor.leads.stages.${stage}`)}
						</option>
					))}
				</select>
			</FormField>

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<FormField
					id="convert-budget-min"
					label={t("realtor.enquiries.budgetMinLabel")}
					error={errors.budgetMinInput}
					hint={t("realtor.enquiries.budgetHint")}
				>
					<Input
						id="convert-budget-min"
						value={form.budgetMinInput}
						onChange={(e) => setField("budgetMinInput", e.target.value)}
						placeholder="50L"
						aria-invalid={errors.budgetMinInput ? true : undefined}
					/>
				</FormField>
				<FormField
					id="convert-budget-max"
					label={t("realtor.enquiries.budgetMaxLabel")}
					error={errors.budgetMaxInput}
				>
					<Input
						id="convert-budget-max"
						value={form.budgetMaxInput}
						onChange={(e) => setField("budgetMaxInput", e.target.value)}
						placeholder="1.2 Cr"
						aria-invalid={errors.budgetMaxInput ? true : undefined}
					/>
				</FormField>
			</div>

			<FormField
				id="convert-notes"
				label={t("realtor.enquiries.convert.notesLabel")}
			>
				<Textarea
					id="convert-notes"
					value={form.notes}
					onChange={(e) => setField("notes", e.target.value)}
					rows={3}
					maxLength={5000}
				/>
			</FormField>

			{convert.isError && (
				<p className="text-sm text-dashboard-pill-risk" role="alert">
					{convert.error.message}
				</p>
			)}

			<DialogFooter className="gap-2">
				<StudioButton variant="secondary" size="sm" onClick={onCancel}>
					{t("realtor.enquiries.cancel")}
				</StudioButton>
				<StudioButton type="submit" size="sm" disabled={convert.isPending}>
					{convert.isPending
						? t("realtor.enquiries.saving")
						: t("realtor.enquiries.convert.submit")}
				</StudioButton>
			</DialogFooter>
		</form>
	);
}
